/**
 * Anonymous progress for the public /typing page, kept in localStorage.
 *
 * Mirrors wasl/typing_tutor/progress.py exactly — drill order enforcement,
 * idempotent completion, best-score ratchet, the unlock chain, the weak-key
 * cutoffs and the adaptive-target builder. If the curriculum or any of those
 * rules change server-side, change them here too.
 *
 * Signed-in users never touch this module: their progress lives in the
 * backend (and gates Majlis posting), so the two stores never merge.
 */

const STORAGE_KEY = "typing.progress.v1";

// Same constants as progress.py.
const CARRIER = "ب"; // a combining mark can't stand alone — hang it on a bāʾ
const WEAK_LIMIT = 8;
const WEAK_MIN_ATTEMPTS = 5;
const ADAPTIVE_GROUPS = 12;

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const data = raw ? JSON.parse(raw) : null;
    if (data && typeof data === "object") {
      return { lessons: data.lessons || {}, keyStats: data.keyStats || {} };
    }
  } catch {
    /* private browsing or corrupt data — start blank */
  }
  return { lessons: {}, keyStats: {} };
}

function save(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* private browsing — progress just won't survive the session */
  }
}

function lessonEntry(data, code) {
  return (
    data.lessons[code] || {
      completed_drills: [],
      best_wpm: 0,
      best_accuracy: 0,
      completed: false,
      completed_at: null,
    }
  );
}

function progressPayload(entry, code, drillKeys) {
  const completed = entry.completed_drills.filter((k) => drillKeys.includes(k));
  const total = drillKeys.length;
  return {
    lesson_code: code,
    completed_drills: completed,
    total_drills: total,
    best_wpm: Math.round(entry.best_wpm * 10) / 10,
    best_accuracy: Math.round(entry.best_accuracy * 10) / 10,
    completed: entry.completed,
    completed_at: entry.completed_at,
    progress_percent: total ? Math.round((completed.length / total) * 100) : 0,
  };
}

/**
 * Record a drill completion (progress.py complete_drill): idempotent, strict
 * lesson order, best scores ratchet upward even on a repeat run.
 *
 * `drillKeys` is the lesson's drill keys in order (from the lesson payload).
 * Returns { progress, error } — error mirrors the server's 409 message.
 */
export function recordDrill(code, drillKeys, drillKey, wpm, accuracy) {
  const data = load();
  const entry = { ...lessonEntry(data, code) };

  if (!drillKeys.includes(drillKey)) {
    return { progress: progressPayload(entry, code, drillKeys), error: "Unknown drill." };
  }

  if (wpm > entry.best_wpm) entry.best_wpm = wpm;
  if (accuracy > entry.best_accuracy) entry.best_accuracy = accuracy;

  let error = null;
  if (!entry.completed_drills.includes(drillKey)) {
    const idx = drillKeys.indexOf(drillKey);
    if (idx > 0 && !entry.completed_drills.includes(drillKeys[idx - 1])) {
      error = "Complete the previous drill first.";
    } else {
      entry.completed_drills = [...entry.completed_drills, drillKey];
      if (drillKeys.every((k) => entry.completed_drills.includes(k)) && !entry.completed) {
        entry.completed = true;
        entry.completed_at = new Date().toISOString();
      }
    }
  }

  data.lessons[code] = entry;
  save(data);
  return { progress: progressPayload(entry, code, drillKeys), error };
}

/** Fold one drill's per-key tallies into the rolling stats (record_key_stats). */
export function recordKeyStats(attempts, errors) {
  if (!attempts || typeof attempts !== "object") return;
  const errs = errors && typeof errors === "object" ? errors : {};
  const data = load();
  for (const [token, rawAttempts] of Object.entries(attempts)) {
    const attemptCount = parseInt(rawAttempts, 10);
    if (!Number.isFinite(attemptCount) || attemptCount <= 0) continue;
    const errorCount = Math.max(0, Math.min(parseInt(errs[token], 10) || 0, attemptCount));
    const stat = data.keyStats[token] || { attempts: 0, errors: 0 };
    data.keyStats[token] = {
      attempts: stat.attempts + attemptCount,
      errors: stat.errors + errorCount,
    };
  }
  save(data);
}

/**
 * Overlay local progress onto the server's (zeroed) lesson list and recompute
 * the unlock chain with the same rule as all_lessons_payload:
 * unlocked = previous lesson completed, or this one already has progress.
 */
export function overlayLessons(serverLessons) {
  const data = load();
  let previousDone = true; // the first lesson is always unlocked
  return serverLessons.map((lesson) => {
    const entry = lessonEntry(data, lesson.code);
    // The server payload doesn't carry drill keys in the list view, so trust
    // the recorded completions and the server's total_drills for the percent.
    const total = lesson.progress?.total_drills || 0;
    const completed = entry.completed_drills;
    const payload = {
      ...lesson.progress,
      completed_drills: completed,
      best_wpm: Math.round(entry.best_wpm * 10) / 10,
      best_accuracy: Math.round(entry.best_accuracy * 10) / 10,
      completed: entry.completed,
      completed_at: entry.completed_at,
      progress_percent: total ? Math.round((completed.length / total) * 100) : 0,
    };
    const out = {
      ...lesson,
      unlocked: previousDone || payload.progress_percent > 0,
      progress: payload,
    };
    previousDone = payload.completed;
    return out;
  });
}

/** Overlay local progress onto a single lesson-detail payload. */
export function overlayLessonProgress(lesson) {
  const data = load();
  const entry = lessonEntry(data, lesson.code);
  const drillKeys = (lesson.drills || []).map((d) => d.key);
  return progressPayload(entry, lesson.code, drillKeys);
}

/** The learner's least accurate keys, worst first (progress.py weak_keys). */
export function weakKeys() {
  const data = load();
  return Object.entries(data.keyStats)
    .map(([token, s]) => {
      const shift = token.startsWith("Shift+");
      return {
        code: shift ? token.slice("Shift+".length) : token,
        shift,
        attempts: s.attempts,
        errors: s.errors,
        accuracy: s.attempts ? ((s.attempts - s.errors) / s.attempts) * 100 : 100,
      };
    })
    .filter((s) => s.errors > 0 && s.attempts >= WEAK_MIN_ATTEMPTS)
    .sort((a, b) => (a.attempts - a.errors) / a.attempts - (b.attempts - b.errors) / b.attempts)
    .slice(0, WEAK_LIMIT);
}

/**
 * Practice text from the weakest keys (progress.py build_adaptive_target).
 * Returns null when there is nothing to go on yet — the caller keeps the
 * server's fallback sentence.
 */
export function buildAdaptiveTarget(layout) {
  const weak = weakKeys();
  if (!weak.length || !layout?.keys) return null;

  const harakat = layout.harakat || {};
  const chunks = [];
  for (const entry of weak) {
    const key = layout.keys[entry.code];
    const char = key ? (entry.shift ? key.shift : key.base) : null;
    if (!char) continue;
    // A mark needs a letter to sit on; a letter can be drilled bare.
    chunks.push(char in harakat ? (CARRIER + char).repeat(2) : char.repeat(3));
  }
  if (!chunks.length) return null;

  return Array.from({ length: ADAPTIVE_GROUPS }, (_, i) => chunks[i % chunks.length]).join(" ");
}
